import { useState, useEffect, useRef, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { QrCode, Camera, Wrench, Image, Video, Truck, Tag, MapPin, CheckCircle, X, Package, Upload, Clock, User, Euro } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const PARKING = {
  P1: { name: "Receptionat", color: "bg-red-500", text: "text-red-400" },
  P2: { name: "Asteptare reparatie", color: "bg-orange-500", text: "text-orange-400" },
  P3: { name: "Spalare si fotografiere", color: "bg-blue-500", text: "text-blue-400" },
  P4: { name: "Licitatie", color: "bg-yellow-500", text: "text-yellow-400" },
  P5: { name: "Showroom", color: "bg-emerald-500", text: "text-emerald-400" },
  P6: { name: "Vandut", color: "bg-gray-500", text: "text-gray-400" },
};

export default function QRScanPage() {
  const { user } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [manualId, setManualId] = useState("");
  const [scooterData, setScooterData] = useState(null);
  const [loading, setLoading] = useState(false);
  const scannerRef = useRef(null);
  const videoRef = useRef(null);

  const handleScan = useCallback(async (scooterId) => {
    if (!scooterId) return;
    setLoading(true);
    setScanning(false);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/qr/scan/${scooterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScooterData(res.data);
      toast.success(`Scuter gasit: ${res.data.brand} ${res.data.model}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scuter negasit");
      setScooterData(null);
    }
    setLoading(false);
  }, []);

  const startCamera = async () => {
    setScanning(true);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (text) => {
          // Parse QR text: could be URL, SCUTERSH:ID, or just ID
          let id = text.trim();
          if (id.includes("SCUTERSH:")) {
            id = id.split("SCUTERSH:")[1].split("|")[0];
          } else if (id.includes("/qr/")) {
            id = id.split("/qr/").pop().split("?")[0];
          } else if (id.includes("scutersh.store/")) {
            id = id.split("/").pop();
          }
          scanner.stop().catch(() => {});
          handleScan(id.trim());
        },
        () => {}
      );
    } catch (err) {
      toast.error("Nu se poate accesa camera");
      setScanning(false);
    }
  };

  const stopCamera = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => { return () => { if (scannerRef.current) scannerRef.current.stop().catch(() => {}); }; }, []);

  const handleAction = async (action, data = {}) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/qr/action/${scooterData.scooter_id}`, { action, ...data }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      handleScan(scooterData.scooter_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare");
    }
  };

  const role = user?.role || "";

  return (
    <Layout>
      <div className="space-y-4 max-w-2xl mx-auto" data-testid="qr-scan-page">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <QrCode className="w-7 h-7 text-primary" /> Scanner QR
        </h1>

        {/* Scanner */}
        {!scooterData && (
          <Card>
            <CardContent className="p-4 space-y-4">
              {scanning ? (
                <div>
                  <div id="qr-reader" className="rounded-lg overflow-hidden" />
                  <Button onClick={stopCamera} variant="outline" className="w-full mt-2"><X className="w-4 h-4 mr-1" /> Opreste camera</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Button onClick={startCamera} className="w-full" size="lg">
                    <Camera className="w-5 h-5 mr-2" /> Scaneaza QR Code
                  </Button>
                  <div className="flex gap-2">
                    <Input placeholder="Sau introduceti ID manual (ex: SC1234)" value={manualId} onChange={e => setManualId(e.target.value)} onKeyDown={e => e.key === "Enter" && handleScan(manualId)} />
                    <Button onClick={() => handleScan(manualId)} disabled={!manualId}>Cauta</Button>
                  </div>
                </div>
              )}
              {loading && <p className="text-center animate-pulse text-primary">Se cauta...</p>}
            </CardContent>
          </Card>
        )}

        {/* Scooter found */}
        {scooterData && (
          <div className="space-y-4">
            {/* Header with parking badge */}
            <Card className={`border-l-4`} style={{ borderLeftColor: PARKING[scooterData.parking]?.color?.replace("bg-", "") || "#666" }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">{scooterData.brand} {scooterData.model} {scooterData.year}</h2>
                    <p className="font-mono text-sm text-muted-foreground">{scooterData.scooter_id} | VIN: {scooterData.vin}</p>
                    <p className="text-sm">{scooterData.mileage?.toLocaleString()} km | {scooterData.engine_cc} cc | {scooterData.color}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={`${PARKING[scooterData.parking]?.color} text-white text-sm px-3 py-1`}>
                      {scooterData.parking} - {PARKING[scooterData.parking]?.name}
                    </Badge>
                    {scooterData.selling_price > 0 && <p className="text-lg font-bold text-primary mt-1">{scooterData.selling_price} EUR</p>}
                  </div>
                </div>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => { setScooterData(null); setManualId(""); }}>
                  <QrCode className="w-4 h-4 mr-1" /> Scaneaza alt scuter
                </Button>
              </CardContent>
            </Card>

            {/* Actions based on role */}
            {scooterData.allowed_actions.includes("parking") && <ParkingAction current={scooterData.parking} onAction={handleAction} role={role} />}
            {scooterData.allowed_actions.includes("fisa_tehnica") && <MechanicAction mc={scooterData.mechanical_check} onAction={handleAction} />}
            {scooterData.allowed_actions.includes("photos") && <PhotoAction scooterId={scooterData.scooter_id} photos={scooterData.photos} videos={scooterData.videos} onRefresh={() => handleScan(scooterData.scooter_id)} />}
            {scooterData.allowed_actions.includes("pret") && <PriceAction current={scooterData.selling_price} onAction={handleAction} />}
            {scooterData.allowed_actions.includes("vandut") && <SoldAction onAction={handleAction} />}
            {scooterData.allowed_actions.includes("livrare") && <DeliveryAction info={scooterData.delivery_info} onAction={handleAction} role={role} />}
            {scooterData.allowed_actions.includes("livrator_status") && <LivratorStatusAction info={scooterData.delivery_info} onAction={handleAction} />}

            {/* History */}
            {scooterData.history?.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-bold mb-2 flex items-center gap-2"><Clock className="w-4 h-4" /> Istoric ({scooterData.history.length})</h3>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {[...scooterData.history].reverse().map((h, i) => (
                      <div key={i} className="flex justify-between text-xs py-1 border-b border-border/20">
                        <span><strong>{h.user}</strong> ({h.role}) — {h.action}</span>
                        <span className="text-muted-foreground">{new Date(h.timestamp).toLocaleString("ro-RO")}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function ParkingAction({ current, onAction, role }) {
  const [selected, setSelected] = useState(current);
  const allowed = role === "admin" ? Object.keys(PARKING) :
    role === "mechanic" ? ["P2", "P3"] :
    role === "organizator" ? ["P3", "P4", "P5"] : Object.keys(PARKING);

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-bold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> Schimba parking</h3>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {Object.entries(PARKING).map(([id, info]) => (
            <button key={id} disabled={!allowed.includes(id)} onClick={() => setSelected(id)}
              className={`p-2 rounded-lg border text-xs font-bold transition-all ${selected === id ? `${info.color} text-white` : allowed.includes(id) ? "border-border hover:border-primary/50" : "opacity-30 cursor-not-allowed"}`}>
              {id}<br /><span className="font-normal text-[10px]">{info.name}</span>
            </button>
          ))}
        </div>
        {selected !== current && (
          <Button onClick={() => onAction("update_parking", { parking: selected })} className="w-full" size="sm">
            Muta in {selected} - {PARKING[selected]?.name}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function MechanicAction({ mc, onAction }) {
  const [notes, setNotes] = useState({
    am_schimbat: mc?.am_schimbat || "",
    am_reparat: mc?.am_reparat || "",
    am_observat: mc?.am_observat || "",
    de_facut: mc?.de_facut || "",
    observatie_generala: mc?.observatie_generala || "",
    constatat_de: mc?.constatat_de || "",
  });
  const [scores, setScores] = useState(mc || {});

  const scoreFields = [
    "motor_stare_generala", "pornire_motor", "compresie_motor", "zgomote_anormale",
    "ambreiaj", "curea_transmisie", "frane_fata", "frane_spate",
    "cadru", "suspensie_fata", "suspensie_spate", "toba_evacuare",
    "ghidon", "carene", "sa", "stare_generala_aspect"
  ];

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <h3 className="font-bold flex items-center gap-2"><Wrench className="w-4 h-4 text-primary" /> Fisa tehnica mecanic</h3>
        
        {/* Score grid */}
        <div className="grid grid-cols-2 gap-2">
          {scoreFields.map(field => (
            <div key={field} className="flex items-center justify-between text-xs">
              <span className="capitalize">{field.replace(/_/g, " ")}</span>
              <div className="flex gap-0.5">
                {[0,1,2,3,4,5].map(v => (
                  <button key={v} onClick={() => setScores(s => ({...s, [field]: v}))}
                    className={`w-6 h-6 rounded text-[10px] font-bold ${scores[field] === v ? ["bg-gray-600","bg-red-500","bg-orange-500","bg-yellow-500","bg-emerald-500","bg-green-400"][v] + " text-white" : "bg-secondary text-muted-foreground"}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Work notes */}
        <div className="space-y-2 bg-secondary/30 rounded-lg p-3">
          {[
            { key: "am_schimbat", label: "Am schimbat" },
            { key: "am_reparat", label: "Am reparat" },
            { key: "am_observat", label: "Am observat" },
            { key: "de_facut", label: "De facut mai departe" },
            { key: "observatie_generala", label: "Observatie generala" },
          ].map(f => (
            <div key={f.key}>
              <Label className="text-xs">{f.label}:</Label>
              <Input value={notes[f.key]} onChange={e => setNotes(n => ({...n, [f.key]: e.target.value}))} placeholder={`${f.label}...`} className="h-8 text-xs" />
            </div>
          ))}
          <div>
            <Label className="text-xs text-red-400">Constatat de * (obligatoriu):</Label>
            <Input value={notes.constatat_de} onChange={e => setNotes(n => ({...n, constatat_de: e.target.value}))} placeholder="Numele dvs." className="h-8 text-xs border-red-500/30" />
          </div>
        </div>

        <Button onClick={() => {
          if (!notes.constatat_de) { toast.error("Numele este obligatoriu!"); return; }
          onAction("update_mechanical", { mechanical_check: scores, work_notes: notes });
        }} className="w-full">
          <CheckCircle className="w-4 h-4 mr-1" /> Salveaza fisa tehnica
        </Button>
      </CardContent>
    </Card>
  );
}

function PhotoAction({ scooterId, photos, videos, onRefresh }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (files, type) => {
    setUploading(true);
    const token = localStorage.getItem("token");
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        if (type === "photo") {
          fd.append("photo_type", "general");
          await axios.post(`${API}/scooters/${scooterId}/photos`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
        } else {
          await axios.post(`${API}/scooters/${scooterId}/videos`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }, timeout: 120000 });
        }
      } catch (e) { toast.error(`Eroare: ${file.name}`); }
    }
    toast.success("Fisiere incarcate!");
    setUploading(false);
    onRefresh();
  };

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-bold mb-2 flex items-center gap-2"><Camera className="w-4 h-4 text-primary" /> Fotografii & Video</h3>
        <div className="flex gap-2 mb-3">
          <label className="flex-1 cursor-pointer">
            <input type="file" multiple accept="image/*" className="hidden" onChange={e => upload(Array.from(e.target.files), "photo")} />
            <Button variant="outline" className="w-full" asChild disabled={uploading}><span><Image className="w-4 h-4 mr-1" /> {uploading ? "..." : `Foto (${photos?.length || 0})`}</span></Button>
          </label>
          <label className="flex-1 cursor-pointer">
            <input type="file" accept="video/*" className="hidden" onChange={e => upload([e.target.files[0]], "video")} />
            <Button variant="outline" className="w-full" asChild disabled={uploading}><span><Video className="w-4 h-4 mr-1" /> {uploading ? "..." : `Video (${videos?.length || 0})`}</span></Button>
          </label>
        </div>
        {photos?.length > 0 && <div className="flex gap-1 overflow-x-auto">{photos.slice(0, 6).map((p, i) => <img key={`p-${p.slice(-8)}`} src={p} alt="" className="w-14 h-14 rounded object-cover" />)}</div>}
      </CardContent>
    </Card>
  );
}

function PriceAction({ current, onAction }) {
  const [price, setPrice] = useState(current || "");
  const [minPrice, setMinPrice] = useState("");
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-bold mb-2 flex items-center gap-2"><Euro className="w-4 h-4 text-primary" /> Pret vanzare</h3>
        <div className="flex gap-2">
          <Input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="Pret EUR" className="flex-1" />
          <Input type="number" value={minPrice} onChange={e => setMinPrice(e.target.value)} placeholder="Pret min" className="flex-1" />
          <Button onClick={() => onAction("set_price", { selling_price: parseFloat(price), min_selling_price: parseFloat(minPrice) || 0 })}>Salveaza</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SoldAction({ onAction }) {
  const [sale, setSale] = useState({ client_name: "", client_phone: "", payment_method: "", amount: "" });
  const [delivery, setDelivery] = useState({ address: "", phone: "", date: "" });
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <h3 className="font-bold flex items-center gap-2"><Tag className="w-4 h-4 text-primary" /> Marcheaza ca vandut</h3>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Nume client" value={sale.client_name} onChange={e => setSale(s => ({...s, client_name: e.target.value}))} />
          <Input placeholder="Telefon client" value={sale.client_phone} onChange={e => setSale(s => ({...s, client_phone: e.target.value}))} />
          <Input placeholder="Suma EUR" type="number" value={sale.amount} onChange={e => setSale(s => ({...s, amount: e.target.value}))} />
          <Select value={sale.payment_method} onValueChange={v => setSale(s => ({...s, payment_method: v}))}>
            <SelectTrigger><SelectValue placeholder="Plata..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="transfer">Transfer</SelectItem>
              <SelectItem value="card">Card</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <h4 className="text-sm font-semibold">Livrare (optional):</h4>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Adresa livrare" value={delivery.address} onChange={e => setDelivery(d => ({...d, address: e.target.value}))} />
          <Input placeholder="Telefon livrare" value={delivery.phone} onChange={e => setDelivery(d => ({...d, phone: e.target.value}))} />
          <Input type="date" value={delivery.date} onChange={e => setDelivery(d => ({...d, date: e.target.value}))} />
        </div>
        <Button onClick={() => onAction("mark_sold", { sale_info: sale, delivery_info: delivery })} className="w-full bg-red-600 hover:bg-red-700">
          Marcheaza VANDUT
        </Button>
      </CardContent>
    </Card>
  );
}

function DeliveryAction({ info, onAction, role }) {
  const [delivery, setDelivery] = useState(info || { address: "", phone: "", date: "", rar_date: "", rar_address: "" });
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <h3 className="font-bold flex items-center gap-2"><Truck className="w-4 h-4 text-primary" /> Informatii livrare / RAR</h3>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Adresa client" value={delivery.address} onChange={e => setDelivery(d => ({...d, address: e.target.value}))} />
          <Input placeholder="Telefon client" value={delivery.phone} onChange={e => setDelivery(d => ({...d, phone: e.target.value}))} />
          <Input placeholder="Data livrare" type="date" value={delivery.date} onChange={e => setDelivery(d => ({...d, date: e.target.value}))} />
          <Input placeholder="Data RAR" type="date" value={delivery.rar_date} onChange={e => setDelivery(d => ({...d, rar_date: e.target.value}))} />
          <Input placeholder="Adresa RAR" value={delivery.rar_address} onChange={e => setDelivery(d => ({...d, rar_address: e.target.value}))} className="col-span-2" />
        </div>
        <Button onClick={() => onAction("update_delivery", { delivery_info: delivery })} className="w-full">Salveaza livrare</Button>
      </CardContent>
    </Card>
  );
}

function LivratorStatusAction({ info, onAction }) {
  const [status, setStatus] = useState(info?.livrator_status || "");
  const [note, setNote] = useState(info?.livrator_note || "");

  const statuses = [
    { value: "livrat", label: "Livrat", color: "bg-emerald-500" },
    { value: "rar_programat", label: "RAR Programat", color: "bg-blue-500" },
    { value: "rar_acceptat", label: "RAR Acceptat", color: "bg-green-500" },
    { value: "rar_refuzat", label: "RAR Refuzat", color: "bg-red-500" },
  ];

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <h3 className="font-bold flex items-center gap-2"><Truck className="w-4 h-4 text-primary" /> Status livrare / RAR</h3>
        
        {info?.livrator_status && (
          <div className="bg-secondary/30 rounded-lg p-2 text-xs">
            Status actual: <strong>{info.livrator_status}</strong>
            {info.livrator_note && <> — {info.livrator_note}</>}
            {info.livrator_user && <> (de {info.livrator_user})</>}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {statuses.map(s => (
            <button key={s.value} onClick={() => setStatus(s.value)}
              className={`p-2 rounded-lg border text-xs font-bold transition-all ${status === s.value ? `${s.color} text-white` : "border-border hover:border-primary/30"}`}>
              {s.label}
            </button>
          ))}
        </div>

        {status === "rar_refuzat" && (
          <div>
            <Label className="text-xs text-red-400">Motiv refuz RAR:</Label>
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder="De ce a fost refuzat..." className="text-xs" />
          </div>
        )}
        {status && status !== "rar_refuzat" && (
          <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Observatii (optional)..." className="text-xs" />
        )}

        <Button onClick={() => {
          if (!status) { toast.error("Selectati un status"); return; }
          onAction("livrator_status", { livrator_status: status, livrator_note: note });
        }} className="w-full" disabled={!status}>
          Salveaza status
        </Button>
      </CardContent>
    </Card>
  );
}

